import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, Table } from 'react-bootstrap';

import WaiitingRoomChartContainer from '../containers/WaitingRoomChartContainer';
import Error from '../../core/components/Error';
import { selectWaitingRooms } from '../selectors/waiting-rooms';
import { deleteWaitingRoomAction, doStartWaitingRoom, doStopWaitingRoom } from '../actions/waiting-rooms';
import { openOverlayer } from '../../core/actions/overlayer';
import { CREATE_WAITING_ROOM_OVERLAYER } from '../constants/overlayers';
import { WAITING_ROOM_RUNNING_STATUS } from '../api/config';

const PAGE_LIMIT = 20;

const renderAnalyticsValue = (waitingRoom, field) => {
    if (waitingRoom.analyticsLoading) {
        return <span className="text-muted">loading...</span>;
    }
    if (!waitingRoom.analyticsData) {
        return '--';
    }
    return waitingRoom.analyticsData[field];
};

const WaitingRoomsList = ({
    setSelectedWaitingRoom,
    currentPage = 1,
    onPageChange,
    searchTerm = '',
    isFetching = false,
    isError = false,
    onSearchChange,
}) => {
    const dispatch = useDispatch();
    const waitingRooms = useSelector(selectWaitingRooms);
    const [viewChartOpenend, setViewChartOpenend] = useState({});

    const onEdit = (waitingRoom) => {
        setSelectedWaitingRoom(waitingRoom);
        dispatch(openOverlayer(CREATE_WAITING_ROOM_OVERLAYER));
    };

    const onViewOutflowChart = (waitingRoom) => {
        setViewChartOpenend({ ...viewChartOpenend, [waitingRoom.id]: !viewChartOpenend[waitingRoom.id] });
    };

    const onDelete = (id) => {
        // eslint-disable-next-line
        window.confirm('Are you sure you want to delete this waiting room?') && dispatch(deleteWaitingRoomAction(id));
    };

    const onStart = (id) => dispatch(doStartWaitingRoom(id));
    const onStop = (id) => dispatch(doStopWaitingRoom(id));
    const hasNextPage = waitingRooms.length === PAGE_LIMIT;
    const handleNextPage = () => onPageChange && onPageChange(currentPage + 1);
    const handlePrevPage = () => onPageChange && onPageChange(Math.max(1, currentPage - 1));
    const renderPagination = () => (
        <div className="d-flex justify-content-between align-items-center mt-3">
            <Button variant="outline-primary" disabled={currentPage === 1} onClick={handlePrevPage}>
                Prev
            </Button>
            <span className="fw-bold">Page {currentPage}</span>
            <Button variant="outline-primary" disabled={!hasNextPage} onClick={handleNextPage}>
                Next
            </Button>
        </div>
    );
    let tableContent = (
        <div className="table-responsive table-waiting-rooms">
            <Table className="table rounded-3">
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Title</th>
                        <th>Inflow</th>
                        <th>Waiting</th>
                        <th>Outflow</th>
                        <th>Max outflow</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {waitingRooms.map((waitingRoom) => (
                        <React.Fragment key={waitingRoom.id}>
                            <tr>
                                <td>
                                    <span className={`${waitingRoom.status === WAITING_ROOM_RUNNING_STATUS ? 'text-success' : 'text-primary'} waiting-room-start text-decoration-none text-center`}>
                                        {waitingRoom.status === WAITING_ROOM_RUNNING_STATUS
                                            ? <i className="bi bi-play-fill" />
                                            : <i className="bi bi-pause-fill" />
                                        }
                                        <span className="d-block">
                                            {waitingRoom.status === WAITING_ROOM_RUNNING_STATUS ? 'Running' : 'Stopped'}
                                        </span>
                                    </span>
                                </td>
                                <td>
                                    <span className="waiting-room-id text-body-tertiary fw-bold d-block">Queue ID {waitingRoom.id}</span>
                                    <span className="pointer" onClick={() => onViewOutflowChart(waitingRoom)}>{waitingRoom.title}</span>
                                </td>
                                <td>{renderAnalyticsValue(waitingRoom, 'inflow')}</td>
                                <td>{waitingRoom.total - waitingRoom.sequence + 1}</td>
                                <td>{renderAnalyticsValue(waitingRoom, 'outflow')}</td>
                                <td>{waitingRoom.outflow}</td>
                                <td>
                                    <div className="d-flex justify-content-center">
                                        <span className={`${waitingRoom.status === WAITING_ROOM_RUNNING_STATUS ? 'text-primary' : 'text-success'}`}>
                                            {waitingRoom.status === WAITING_ROOM_RUNNING_STATUS
                                                ? <i className="bi bi-pause-fill me-2 pointer" onClick={() => onStop(waitingRoom.id)} />
                                                : <i className="bi bi-play-fill me-2 pointer" onClick={() => onStart(waitingRoom.id)} />
                                            }
                                        </span>
                                        <span onClick={() => onViewOutflowChart(waitingRoom)}><i className="bi bi-graph-up me-2 pointer"></i></span>
                                        <span onClick={() => window.open(`/waiting-room/preview/${waitingRoom.id}`, '_blank')}><i className="bi bi-eye me-2 pointer"></i></span>
                                        <span onClick={() => onEdit(waitingRoom)}><i className="bi bi-pencil-square me-2 pointer"></i></span>
                                        <span onClick={() => onDelete(waitingRoom.id)}><i className="bi bi-trash me-2 pointer"></i></span>
                                    </div>
                                </td>
                            </tr>
                            {viewChartOpenend[waitingRoom.id]
                                && <tr>
                                    <td colSpan={9}>
                                        <WaiitingRoomChartContainer
                                            id={waitingRoom.id} maxOutflow={waitingRoom.outflow}
                                        />
                                    </td>
                                </tr>
                            }
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>
        </div>
    );

    if (isError) {
        tableContent = (
            <div className="d-flex justify-content-center">
                <Error />
            </div>
        );
    } else if (isFetching) {
        tableContent = (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div className="loading-spinner" aria-label="Loading waiting rooms" />
                <p className="mb-0 text-muted">Loading waiting rooms...</p>
            </div>
        );
    } else if (!waitingRooms.length) {
        tableContent = <p className="mb-3">No waiting rooms found!</p>;
    }

    return (
        <>
            <div className="title-container d-flex align-items-md-center justify-content-md-between mt-5 mb-4">
                <h2 className="fw-bold mb-0 mt-0">Manage waiting rooms</h2>
                <Button variant="primary" onClick={() => dispatch(openOverlayer(CREATE_WAITING_ROOM_OVERLAYER))}>Create waiting room</Button>
            </div>
            <Form.Control
                type="text"
                placeholder="Search by name or queue ID..."
                className="mb-3"
                value={searchTerm}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            />
            <div className="bg-white p-3 rounded-3 mt-3 mb-5">
                {tableContent}
                {!isFetching && !isError && renderPagination()}
            </div>
        </>
    );
};

export default WaitingRoomsList;
